import useAuthStore from '@/stores/authStore'
import { waitForPeerSessionHydration } from '@/composables/useAuthTabSync'
import { getDatadogRum } from '@/plugins/datadog'
import type { RouteLocationNormalized, NavigationGuardReturn } from 'vue-router'
import pwa from '@/utils/pwa'

// Guard structure mirrors ada-app-ui so guards added during view migration
// slot in without a rewrite. Only the auth gate is relevant for the customer
// portal — ada-app-ui's 7 personal/OAuth/creditor guards are deliberately
// not ported.

declare module 'vue-router' {
  interface RouteMeta {
    /** Route is reachable without a selected token (e.g. Authenticate, Dev). */
    public?: boolean
  }
}

export interface GuardContext {
  authStore: ReturnType<typeof useAuthStore>
  selectedToken: ReturnType<typeof useAuthStore>['selectedToken']
  isAuthenticated: boolean
}

export type GuardResult = NavigationGuardReturn | undefined
export type Guard = (to: RouteLocationNormalized, ctx: GuardContext) => GuardResult | Promise<GuardResult>

/**
 * Simplified auth gate — customer-ui doesn't classify tokens, so the
 * allowedTokenTypes matrix collapses to a boolean meta.public check.
 */
async function guardAuth(to: RouteLocationNormalized, ctx: GuardContext): Promise<GuardResult> {
  if (to.meta.public) return
  if (ctx.isAuthenticated) return
  // A freshly opened tab may be about to receive the session from an open peer
  // tab (useAuthTabSync request-session handshake). Hold the decision until the
  // handshake settles so a deep link isn't bounced to /authenticate — the
  // legacy token watcher would then push it on to Home and the link is lost.
  // The promise is ONLY a wait: it memoizes the startup handshake, so its value
  // must never serve as an auth pass (it would still say true after a later
  // idle logout). Decide from live store state.
  await waitForPeerSessionHydration()
  if (ctx.authStore.selectedToken != null) return
  return { name: 'Authenticate' }
}

/**
 * LEGACY: OCR deep-link side effect (mutation, not a redirect — always returns
 * undefined). /ocr/{ocr} redirects to Home and the suggested OCR number is
 * stashed in the legacy Vuex payment state for the old payment flow.
 */
async function guardOcrSideEffect(to: RouteLocationNormalized): Promise<GuardResult> {
  if (to.redirectedFrom?.name === 'OCR') {
    const { store } = await import('@old/store')
    store.state.payment = { ocr: { ocr: '', suggested_ocr: to.redirectedFrom.params.ocr } }
  }
}

export const allGuards: Guard[] = [guardOcrSideEffect, guardAuth]

function createGuardContext(authStore: ReturnType<typeof useAuthStore>): GuardContext {
  const selectedToken = authStore.selectedToken

  return {
    authStore,
    selectedToken,
    isAuthenticated: selectedToken != null,
  }
}

/**
 * Validates a route against guards. Returns early with a redirect/denial if any guard fails,
 * or undefined if all guards pass. Accepts a subset of guards.
 */
export async function validateRouteGuards(
  route: RouteLocationNormalized,
  guards: Guard[] = allGuards
): Promise<GuardResult> {
  const authStore = useAuthStore()
  const ctx = createGuardContext(authStore)

  for (const guard of guards) {
    const result = await guard(route, ctx)
    if (result != null) return result
  }
}

/**
 * Registers all route guards and hooks with the router.
 * Call this once during app initialization.
 */
export function setupRouterGuards(router: ReturnType<typeof import('vue-router').createRouter>) {
  for (const guard of allGuards) {
    router.beforeEach(async (to) => {
      const authStore = useAuthStore()
      const ctx = createGuardContext(authStore)
      const guardResult = await guard(to, ctx)
      if (guardResult != null) return guardResult
      return true
    })
  }

  router.afterEach(async (to) => {
    // Forced reload on new deploys — customers get fresh assets on the
    // next navigation instead of 404s on removed hashed chunks.
    if (pwa.needRefresh.value) {
      console.log('New version of the app is available, refreshing to update')
      await pwa.updateServiceWorker()
      window.location.reload()
      return
    }

    // Datadog RUM view tracking (trimmed — no ItemDisplay/resource naming, no
    // user-activity tracking; customer portal has no userActivity util)
    getDatadogRum()?.startView({
      name: typeof to.name === 'string' ? to.name : to.path,
      context: to.params,
    })
  })
}
